import ApiConfig from '../api/config'
import axios from 'axios'
import { store } from '../store/store'
import router from '../router'
import * as actionTypes from '@/store/action-types'
import qs from 'qs'

axios.defaults.withCredentials = true;

// Persistent device identifier used to recognise "trusted" devices across two-factor
// logins. A generated UUID is used instead of the User-Agent header, since the
// User-Agent string changes whenever the browser auto-updates and would otherwise
// invalidate a device that was already trusted.
function getDeviceId() {
  try {
    let deviceId = localStorage.getItem('device_id')
    if (!deviceId) {
      deviceId = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`)
      localStorage.setItem('device_id', deviceId)
    }
    return deviceId
  } catch (e) {
    return null
  }
}

const api = axios.create({
  baseURL: ApiConfig.basePath,
  withCredentials: true // Required for cookies to be sent/received
})

// separate axios instance for long-running AI operations (landing pages)
const aiApi = axios.create({
  baseURL: ApiConfig.basePath,
  timeout: 360000, // 6 minutes for AI generation
  withCredentials: true // Required for cookies to be sent/received
})

// Request interceptor for main api
api.interceptors.request.use((config) => {
  const token = JSON.parse(localStorage.getItem('token'))
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  const deviceId = getDeviceId()
  if (deviceId) {
    config.headers['X-Device-Id'] = deviceId
  }
  return config
})

// Request interceptor for AI api
aiApi.interceptors.request.use((config) => {
  const token = JSON.parse(localStorage.getItem('token'))
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor for main api
const AUTH_ERROR_TYPES = ['token_expired', 'token_invalid', 'unauthenticated', 'suspended_account']
api.interceptors.response.use(
  (response) => {
    const errorType = response.data?.meta?.error_type
    if (errorType && AUTH_ERROR_TYPES.includes(errorType)) {
      store.dispatch(actionTypes.LOGOUT_USER).then(() => {
        router.push({ name: 'login' })
      })
    }
    return response
  },
  (error) => {
    const errorType = error.response?.data?.meta?.error_type
    if (error.response?.data?.meta?.code == 401 && AUTH_ERROR_TYPES.includes(errorType)) {
      store.dispatch(actionTypes.LOGOUT_USER).then(() => {
        router.push({ name: 'login' })
      })
    }
    return Promise.reject(error)
  }
)

// Response interceptor for AI api
aiApi.interceptors.response.use(
  (response) => {
    const errorType = response.data?.meta?.error_type
    if (errorType && AUTH_ERROR_TYPES.includes(errorType)) {
      store.dispatch(actionTypes.LOGOUT_USER).then(() => {
        router.push({ name: 'login' })
      })
    }
    return response
  },
  (error) => {
    const errorType = error.response?.data?.meta?.error_type
    if (error.response?.data?.meta?.code == 401 && AUTH_ERROR_TYPES.includes(errorType)) {
      store.dispatch(actionTypes.LOGOUT_USER).then(() => {
        router.push({ name: 'login' })
      })
    }
    return Promise.reject(error)
  }
)

// Helper function to check if route is for AI landing pages
function isAiLandingPageRoute(url) {
  return url.includes('landing-pages') && (
    url === 'landing-pages' || 
    url.includes('/regenerate')
  )
}

// GET request function (unchanged)
export function getData(url, config = {}) {
  return api.get(url, config)
}

// POST request function with AI landing page detection
export function postData(url, data, config = {}) {
  if (isAiLandingPageRoute(url)) {
    return config?.emulateJSON
      ? encodeAndSendData('POST', url, data, true) // Pass true for AI api
      : aiApi.post(url, data, config)
  }
  
  // Use regular api for all other routes
  return config?.emulateJSON
    ? encodeAndSendData('POST', url, data)
    : api.post(url, data, config)
}

// PUT request function
export function putData(url, data, config = {}) {
  return config?.emulateJSON
    ? encodeAndSendData('PUT', url, data)
    : api.put(url, data, config)
}

// DELETE request function
export function deleteData(url, data = null, config = {}) {
  if (data) {
    return api.delete(url, { data, ...config })
  }
  return api.delete(url, config)
}

// Helper function with optional AI api support
function encodeAndSendData(method, url, data, useAiApi = false) {
  const apiInstance = useAiApi ? aiApi : api
  return apiInstance({
    method,
    url,
    data: qs.stringify(data),
    headers: {
      'content-type': 'application/x-www-form-urlencoded; charset=utf-8',
    }
  })
}